// src/components/Header.tsx
import React, { useEffect, useState } from "react";
import { Layout, Row, Col, Typography, Button, Dropdown, Menu, Space, Modal, Descriptions, Spin, Grid } from "antd";
import { UserOutlined, LogoutOutlined, DownOutlined } from "@ant-design/icons";
import { doc, getDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ADMIN_ROLE, STANDARD_ROLE } from "../constants/roles";
import { db } from "../firebase/firebase";

const { Header } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const roleLabel = (role: string | null): string => {
  switch (role) {
    case ADMIN_ROLE:
      return "管理者 (admin)";
    case STANDARD_ROLE:
      return "一般 (standard)";
    default:
      return role || "-";
  }
};

const formatDateTime = (value: unknown): string => {
  if (value == null || value === "") return "-";
  if (typeof value === "number" && Number.isFinite(value)) {
    return new Date(value).toLocaleString("ja-JP");
  }
  if (typeof value === "string") {
    const asNumber = Number(value);
    if (Number.isFinite(asNumber) && asNumber > 0) {
      return new Date(asNumber).toLocaleString("ja-JP");
    }
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return new Date(parsed).toLocaleString("ja-JP");
    }
  }
  if (typeof value === "object" && value !== null && "toDate" in value) {
    const toDate = (value as { toDate?: () => Date }).toDate;
    if (typeof toDate === "function") {
      return toDate().toLocaleString("ja-JP");
    }
  }
  return "-";
};

type ProfileDetails = {
  userId: string;
  phoneNumber: string;
  others: string;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string;
};

const emptyProfileDetails: ProfileDetails = {
  userId: "-",
  phoneNumber: "-",
  others: "-",
  createdAt: "-",
  updatedAt: "-",
  lastLoginAt: "-",
};

const AppHeader: React.FC = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { currentUser, userRole, displayName, logout, companyName, orgName } = useAuth();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileDetails, setProfileDetails] = useState<ProfileDetails>(emptyProfileDetails);

  const userName =
    displayName ||
    currentUser?.displayName ||
    currentUser?.email?.split("@")[0] ||
    "ゲスト";

  const getDisplayMode = (): string => {
    switch (userRole) {
      case ADMIN_ROLE:
        return `管理者${companyName ? `（${companyName}）` : ""}`;
      case STANDARD_ROLE: {
        let userInfo = "ユーザー";
        const companyAndOrg = [companyName, orgName].filter(Boolean).join(" ");
        if (companyAndOrg) {
          userInfo += `（${companyAndOrg}）`;
        }
        return userInfo;
      }
      default:
        return "ゲスト";
    }
  };

  const displayMode = getDisplayMode();

  useEffect(() => {
    if (!profileOpen || !currentUser?.uid) return;

    let cancelled = false;
    const loadProfile = async () => {
      setProfileLoading(true);
      try {
        const userDoc = await getDoc(doc(db, "loginusers", currentUser.uid));
        const data = userDoc.exists() ? userDoc.data() : {};
        const authCreatedAt = currentUser.metadata?.creationTime
          ? formatDateTime(currentUser.metadata.creationTime)
          : "-";
        const authLastSignIn = currentUser.metadata?.lastSignInTime
          ? formatDateTime(currentUser.metadata.lastSignInTime)
          : "-";

        if (cancelled) return;
        setProfileDetails({
          userId: (typeof data.userId === "string" && data.userId.trim()) || "-",
          phoneNumber: (typeof data.phoneNumber === "string" && data.phoneNumber.trim()) || "-",
          others: (typeof data.Others === "string" && data.Others.trim()) || "-",
          createdAt: formatDateTime(data.createdAt) !== "-"
            ? formatDateTime(data.createdAt)
            : authCreatedAt,
          updatedAt: formatDateTime(data.updatedAt),
          lastLoginAt: formatDateTime(data.lastLoginAt) !== "-"
            ? formatDateTime(data.lastLoginAt)
            : authLastSignIn,
        });
      } catch (error) {
        console.error("プロフィール取得エラー:", error);
        if (!cancelled) {
          setProfileDetails({
            ...emptyProfileDetails,
            createdAt: currentUser.metadata?.creationTime
              ? formatDateTime(currentUser.metadata.creationTime)
              : "-",
            lastLoginAt: currentUser.metadata?.lastSignInTime
              ? formatDateTime(currentUser.metadata.lastSignInTime)
              : "-",
          });
        }
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    };

    void loadProfile();
    return () => {
      cancelled = true;
    };
  }, [profileOpen, currentUser]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("ログアウトエラー:", error);
    }
  };

  const userMenu = (
    <Menu>
      <Menu.Item
        key="profile"
        icon={<UserOutlined />}
        onClick={() => setProfileOpen(true)}
      >
        プロフィール
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
        ログアウト
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <Header style={{ backgroundColor: "#1565c0", color: "white", padding: "10px" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Typography.Title level={4} style={{ margin: 0, color: "white" }}>
              Care Tablet Web
            </Typography.Title>
          </Col>
          <Col>
            <Space>
              {!isMobile && (
                <Text style={{ color: "white", marginRight: 16 }}>
                  モード：{displayMode}
                </Text>
              )}
              <Dropdown overlay={userMenu} trigger={["click"]}>
                <Button type="text" style={{ color: "white" }}>
                  <Space>
                    <UserOutlined />
                    {isMobile ? userName.slice(0, 10) : userName}
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            </Space>
          </Col>
        </Row>
      </Header>

      <Modal
        title="プロフィール"
        open={profileOpen}
        onCancel={() => setProfileOpen(false)}
        afterClose={() => setProfileDetails(emptyProfileDetails)}
        footer={[
          <Button key="close" type="primary" onClick={() => setProfileOpen(false)}>
            閉じる
          </Button>,
        ]}
        destroyOnClose
      >
        <Spin spinning={profileLoading}>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="表示名">{userName}</Descriptions.Item>
            <Descriptions.Item label="メールアドレス">
              {currentUser?.email || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="ロール">{roleLabel(userRole)}</Descriptions.Item>
            <Descriptions.Item label="ユーザーID">{profileDetails.userId}</Descriptions.Item>
            <Descriptions.Item label="電話番号">{profileDetails.phoneNumber}</Descriptions.Item>
            <Descriptions.Item label="登録日">{profileDetails.createdAt}</Descriptions.Item>
            <Descriptions.Item label="更新日">{profileDetails.updatedAt}</Descriptions.Item>
            <Descriptions.Item label="最終ログイン">{profileDetails.lastLoginAt}</Descriptions.Item>
            <Descriptions.Item label="備考">{profileDetails.others}</Descriptions.Item>
            <Descriptions.Item label="ユーザーUID">
              {currentUser?.uid || "-"}
            </Descriptions.Item>
          </Descriptions>
        </Spin>
      </Modal>
    </>
  );
};

export default AppHeader;
